import { justForSideEffectWithPayload } from 'src/infrastructure/saga.helpers'
import {
  CancelSwapOrReturnPayload,
  CountBatchesToReturnPayload,
  CountBatchesToReturnSagaPayload,
  CreateOrUpdateRequestPayload,
  FinishSwapOrReturnToResolvePayload,
  GetDataForRequestCreationPayload,
  GetSwapsOrReturnsPayload,
  PendingData,
  Product,
  ReleaseReturnRequestPayload,
  SetBatchesPayload,
  SetProperty,
  StartOrDelaySwapOrReturnToResolvePayload,
  SwapAddOrRemoveReceiveBatchesPayload,
  SwapOrReturnState,
  SwapOrReturnStatusEnum,
  SwapOrReturnToResolveItemNotFoundPayload,
  SwapOrReturnTypeIds,
  UpdateItemBatchesToReceivePayload,
  ValidateRequestsToResolvePayload,
  ValidateSupplierReleasePayload,
  ValidateSwapOrReturnToResolvePayload,
} from './SwapOrReturnTypes'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

export const initialState: SwapOrReturnState = {
  swapOrReturnInvoices: [],
  filteredStatus: [],
  swapOrReturnData: {
    swap: null,
    return: null,
    availableProductsForCreation: [],
    infoReturnOptions: null,
    invoiceKey: null,
    invoiceNumber: null,
    supplierName: null,
  },
  selectedProducts: [],
  requestTagInfo: [],
  swapOrReturnToResolveIds: [],
  swapOrReturnToResolveData: null,
  swapOrReturnSearchData: undefined,
  pendingsToPrint: undefined,
  pagination: {
    currentPage: 1,
    perPage: 25,
    totalItems: 0,
    totalPages: 0,
  },
}

const getSwapsOrReturns = justForSideEffectWithPayload<GetSwapsOrReturnsPayload>()
const cancelSwapOrReturn = justForSideEffectWithPayload<CancelSwapOrReturnPayload>()
const getDataForRequestCreation = justForSideEffectWithPayload<GetDataForRequestCreationPayload>()
const getRequestProductsToEdit = justForSideEffectWithPayload<number>()
const createOrUpdateRequest = justForSideEffectWithPayload<CreateOrUpdateRequestPayload>()
const getSwapOrReturnToResolve = justForSideEffectWithPayload<number>()
const startSwapOrReturnToResolve = justForSideEffectWithPayload<StartOrDelaySwapOrReturnToResolvePayload>()
const delaySwapOrReturnToResolve = justForSideEffectWithPayload<StartOrDelaySwapOrReturnToResolvePayload>()
const validateSwapOrReturnToResolve = justForSideEffectWithPayload<ValidateSwapOrReturnToResolvePayload>()
const countBatchesToReturn = justForSideEffectWithPayload<CountBatchesToReturnSagaPayload>()
const swapAddOrRemoveReceiveBatches = justForSideEffectWithPayload<SwapAddOrRemoveReceiveBatchesPayload>()
const undoItemCount = justForSideEffectWithPayload<number>()
const finishSwapOrReturnToResolve = justForSideEffectWithPayload<FinishSwapOrReturnToResolvePayload>()
const swapOrReturnToResolveItemNotFound = justForSideEffectWithPayload<SwapOrReturnToResolveItemNotFoundPayload>()
const validateRequestsToResolve = justForSideEffectWithPayload<ValidateRequestsToResolvePayload>()
const validateSupplierRelease = justForSideEffectWithPayload<ValidateSupplierReleasePayload>()
const releaseReturnRequest = justForSideEffectWithPayload<ReleaseReturnRequestPayload>()

const setSwapOrReturnToCancelStatus = (state: SwapOrReturnState, action: PayloadAction<number>): SwapOrReturnState => ({
  ...state,
  swapOrReturnInvoices: state.swapOrReturnInvoices.map((invoice) => {
    if (invoice.id === action.payload) {
      return {
        ...invoice,
        status: SwapOrReturnStatusEnum.CANCELED,
      }
    }
    return invoice
  }),
})

const removeSwapOrReturnInvoice = (state: SwapOrReturnState, action: PayloadAction<number>): SwapOrReturnState => ({
  ...state,
  swapOrReturnInvoices: state.swapOrReturnInvoices.filter((invoice) => invoice.id !== action.payload),
})

const setSelectProduct = (state: SwapOrReturnState, action: PayloadAction<Product>): SwapOrReturnState => {
  const product = action.payload
  const availableProductsForCreation = state.swapOrReturnData.availableProductsForCreation.filter((p) => p.idModel !== product.idModel)
  const selectedProducts = [...state.selectedProducts, { ...product, inputQuantity: undefined, isQuantityMatch: false }].sort((a, b) => a.idModel - b.idModel)

  return {
    ...state,
    swapOrReturnData: { ...state.swapOrReturnData, availableProductsForCreation },
    selectedProducts,
  }
}

const setDeselectProduct = (state: SwapOrReturnState, action: PayloadAction<Product>): SwapOrReturnState => {
  const product = action.payload
  const selectedProducts = state.selectedProducts.filter((p) => p.idModel !== product.idModel)

  const batchesWithResetInputQuantities = product.batches.map((batch) => ({ ...batch, inputQuantity: null }))

  const availableProductsForCreation = [
    ...state.swapOrReturnData.availableProductsForCreation, { ...product, batches: batchesWithResetInputQuantities },
  ].sort((a, b) => a.idModel - b.idModel)

  return {
    ...state,
    swapOrReturnData: { ...state.swapOrReturnData, availableProductsForCreation },
    selectedProducts,
  }
}

const setInputQuantity = (state: SwapOrReturnState, action: PayloadAction<{ product: Product; inputQuantity?: number }>): SwapOrReturnState => {
  const { product, inputQuantity } = action.payload
  const totalInputQuantity = product.batches.reduce((acc, item) => acc + (item.inputQuantity || 0), 0)
  const isQuantityMatch = inputQuantity === totalInputQuantity && inputQuantity > 0
  const selectedProducts = state.selectedProducts.map((p) => (p.idModel === product.idModel ? { ...p, inputQuantity, isQuantityMatch } : p))

  return {
    ...state,
    selectedProducts,
  }
}

const setBatches = (state: SwapOrReturnState, action: PayloadAction<SetBatchesPayload>): SwapOrReturnState => {
  const { batches, idModel, inputQuantity } = action.payload
  const totalInputQuantity = batches.reduce((acc, item) => acc + (item.inputQuantity || 0), 0)
  const isQuantityMatch = inputQuantity === totalInputQuantity && inputQuantity > 0
  const selectedProducts = state.selectedProducts.map((p) => (p.idModel === idModel ? { ...p, batches, isQuantityMatch } : p))

  return {
    ...state,
    selectedProducts,
  }
}

const updateItemBatchesToReturn = (state: SwapOrReturnState, action: PayloadAction<CountBatchesToReturnPayload>): SwapOrReturnState => {
  const { idItem, batches } = action.payload

  if (state.swapOrReturnToResolveData) {
    const item = state.swapOrReturnToResolveData.items.find((p) => p.idItem === idItem)

    if (item) {
      batches.forEach((batch) => {
        const batchToUpdate = item.returnBatches.find((b) => b.idRequestItemBatch === batch.idRequestItemBatch)
        if (batchToUpdate) {
          batch.expirationBatches.forEach((expirationBatch) => {
            const expirationBatchToUpdate = batchToUpdate.expirationBatches.find((eb) => eb.idExpirationBatch === expirationBatch.idExpirationBatch)
            if (expirationBatchToUpdate) {
              expirationBatchToUpdate.countedQuantity = expirationBatch.countedQuantity
            }
          })
          batchToUpdate.countedQuantity = batch.quantity
        }
      })

      item.returnQuantityCounted = true
    }
  }

  return state
}

const updateItemBatchesToReceive = (state: SwapOrReturnState, action: PayloadAction<UpdateItemBatchesToReceivePayload>): SwapOrReturnState => {
  const { idItem, batches } = action.payload

  if (state.swapOrReturnToResolveData) {
    const item = state.swapOrReturnToResolveData.items.find((p) => p.idItem === idItem)

    if (item) {
      item.receiveBatches = batches
      item.receiveQuantityCounted = true
    }
  }

  return state
}

const resetCountedQuantity = (state: SwapOrReturnState, action: PayloadAction<number>): SwapOrReturnState => {
  if (state.swapOrReturnToResolveData) {
    const item = state.swapOrReturnToResolveData.items.find((p) => p.idItem === action.payload)

    if (item) {
      item.returnBatches.forEach((batch) => {
        batch.countedQuantity = undefined
      })
      item.returnQuantityCounted = false
      item.receiveBatches = undefined
      item.receiveQuantityCounted = state.swapOrReturnToResolveData.idType === SwapOrReturnTypeIds.SWAP ? false : undefined
    }
  }
  return state
}

const swapOrReturnToResolveDataUpdateStatus = (state: SwapOrReturnState, action: PayloadAction<string>): SwapOrReturnState => {
  if (state.swapOrReturnToResolveData) {
    return {
      ...state,
      swapOrReturnToResolveData: {
        ...state.swapOrReturnToResolveData,
        status: action.payload,
      },
    }
  }
  return state
}

const setProductNotFound = (state: SwapOrReturnState, action: PayloadAction<number>): SwapOrReturnState => {
  if (state.swapOrReturnToResolveData) {
    const item = state.swapOrReturnToResolveData.items.find((p) => p.idItem === action.payload)

    if (item) {
      item.notFound = true
    }
  }
  return state
}

const resetAllCountedQuantities = (state: SwapOrReturnState): SwapOrReturnState => {
  const { swapOrReturnToResolveData } = state

  if (swapOrReturnToResolveData) {
    swapOrReturnToResolveData.items.forEach((item) => {
      item.returnBatches.forEach((batch) => {
        batch.countedQuantity = undefined
      })
      item.returnQuantityCounted = false
      item.receiveBatches = undefined
      item.receiveQuantityCounted = swapOrReturnToResolveData.idType === SwapOrReturnTypeIds.SWAP ? false : undefined
      item.notFound = undefined
    })
  }
  return state
}

const setSelectedIdToResolve = (state: SwapOrReturnState, action: PayloadAction<number>): SwapOrReturnState => {
  const { swapOrReturnToResolveIds } = state
  const requestId = action.payload

  return {
    ...state,
    swapOrReturnToResolveIds: [...swapOrReturnToResolveIds, requestId],
  }
}

const removeSelectedIdToResolve = (state: SwapOrReturnState, action: PayloadAction<number>): SwapOrReturnState => {
  const { swapOrReturnToResolveIds } = state
  const requestId = action.payload

  return {
    ...state,
    swapOrReturnToResolveIds: swapOrReturnToResolveIds.filter((id) => id !== requestId),
  }
}

const setPendingToPrintAsDone = (state: SwapOrReturnState, action: PayloadAction<PendingData>): SwapOrReturnState => {
  const pending = state.pendingsToPrint?.pendings.find((p) => p.idReceivement === action.payload.idReceivement && p.idRequest === action.payload.idRequest)

  if (pending) {
    pending.done = true
  }

  return state
}

const setProperty = (state: SwapOrReturnState, action: PayloadAction<SetProperty>): SwapOrReturnState => {
  const { property, value } = action.payload

  return {
    ...state,
    [property]: value,
  }
}

const clearState = (): SwapOrReturnState => initialState

const slice = createSlice({
  name: 'swapOrReturn',
  initialState,
  reducers: {
    getSwapsOrReturns,
    cancelSwapOrReturn,
    getDataForRequestCreation,
    getRequestProductsToEdit,
    createOrUpdateRequest,
    getSwapOrReturnToResolve,
    startSwapOrReturnToResolve,
    delaySwapOrReturnToResolve,
    validateSwapOrReturnToResolve,
    validateSupplierRelease,
    countBatchesToReturn,
    swapAddOrRemoveReceiveBatches,
    undoItemCount,
    finishSwapOrReturnToResolve,
    swapOrReturnToResolveItemNotFound,
    validateRequestsToResolve,
    releaseReturnRequest,
    setSwapOrReturnToCancelStatus,
    removeSwapOrReturnInvoice,
    setSelectProduct,
    setDeselectProduct,
    setInputQuantity,
    setBatches,
    updateItemBatchesToReturn,
    updateItemBatchesToReceive,
    resetCountedQuantity,
    swapOrReturnToResolveDataUpdateStatus,
    setProductNotFound,
    resetAllCountedQuantities,
    setSelectedIdToResolve,
    removeSelectedIdToResolve,
    setPendingToPrintAsDone,
    setProperty,
    clearState,
  },
})

export default slice.reducer

export const { actions } = slice
